<template>
    <div class="relative">

        <!-- Full-width sticky top bar -->
        <div v-if="canCreateArticle"
            class="sticky top-0 z-30 w-full bg-white/30 backdrop-blur-sm shadow-sm flex justify-end px-4 py-3">

            <button @click="openCreateModal" class="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600
                       hover:from-orange-600 hover:to-orange-700 text-white font-semibold
                       px-5 py-2 rounded-lg shadow-lg transform hover:-translate-y-0.5 transition
                       duration-200 ease-in-out">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Create Article
            </button>

        </div>

        <!-- Full-page modal -->
        <CreateArticleModal v-model="showModal" :mode="modalMode" :articleData="currentArticle"
            @save="handleSaveArticle" />

        <!-- ARTICLES LIST -->
        <ArticleList :articles="articles" @edit="editArticle" />

    </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2";

import { getAllArticles, createArticle, updateArticle } from "@/api/article";
import CreateArticleModal from "@/components/modals/CreateArticleModal.vue";
import ArticleList from "@/components/ui/ArticleList.vue";

export default {
    components: { CreateArticleModal, ArticleList },

    setup() {
        const showModal = ref(false);
        const modalMode = ref("create");
        const currentArticle = ref(null);
        const articles = ref([]);
        const loadingArticles = ref(false);

        const mode = ref("cms");
        const store = useStore();
        const user = computed(() => store.state.auth.user);

        const canCreateArticle = computed(() => {
            return ["super", "admin", "creator", "coordinator"].includes(user.value?.gazetteRole);
        });

        const fetchArticles = async () => {
            loadingArticles.value = true;
            try {
                const { data } = await getAllArticles({ mode: mode.value });
                articles.value = data.data || [];
            } catch (err) {
                console.error("[Article] Failed to fetch:", err);
            } finally {
                loadingArticles.value = false;
            }
        };

        const openCreateModal = () => {
            modalMode.value = "create";
            currentArticle.value = null;
            showModal.value = true;
        };

        const editArticle = (article) => {
            currentArticle.value = article;
            modalMode.value = "edit";
            showModal.value = true;
        };

        const handleSaveArticle = async (payload) => {
            try {
                // Show SweetAlert with big hourglass
                Swal.fire({
                    title: payload.id ? "Updating article..." : "Saving article...",
                    allowOutsideClick: false,
                    showConfirmButton: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Prepare FormData
                const form = new FormData();
                Object.entries(payload).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        form.append(key, value);
                    }
                });

                // Simulate delay for visible loading (optional)
                await new Promise((r) => setTimeout(r, 800));

                // API call
                if (payload.id) {
                    await updateArticle(payload.id, form);
                } else {
                    await createArticle(form);
                }

                // Refresh articles
                await fetchArticles();

                // Success alert
                Swal.fire({
                    icon: "success",
                    title: payload.id ? "Article Updated" : "Article Created",
                    text: payload.id
                        ? "The article was successfully updated."
                        : "The article was successfully created.",
                    timer: 2000,
                    showConfirmButton: false
                });

                showModal.value = false;

            } catch (err) {
                console.error("[Article] Failed to save:", err);
                Swal.fire({
                    icon: "error",
                    title: "Save Failed",
                    text: err.response?.data?.message || "Failed to save article."
                });
            }
        };

        onMounted(fetchArticles);

        return {
            mode,
            user,
            articles,
            showModal,
            modalMode,
            currentArticle,
            loadingArticles,
            canCreateArticle,
            editArticle,
            openCreateModal,
            handleSaveArticle
        };
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>